import { Loader2Icon, SearchIcon, XIcon } from 'lucide-react';
import { type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';

/**
 * The search box at the top of a place picker's tree — SharePoint and OneDrive alike, so the two
 * halves of one dialog look and behave the same. It searches on Enter or a click on the magnifier,
 * not on every keystroke: each search is a Microsoft Search call, and a half-typed word is not a
 * question.
 *
 * The magnifier is the button. Enter is not something a person finds on their own, and a second
 * magnifier on the right would read as a duplicate — so the one already there does the job, and
 * says so by lighting up once there is something to search for. The box is locked while a
 * search runs, so the results on screen always answer the words in it.
 */
export function PickerSearchBox({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder,
  label,
  searching,
  showClear,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  placeholder: string;
  label: string;
  /**
   * Any request the finder has in flight, a Load more included. The box, the magnifier and Clear
   * all wait for it, so no answer can land on a search that was cleared or replaced meanwhile.
   */
  searching: boolean;
  /** Whether there is anything to clear — typed text, or results on screen. */
  showClear: boolean;
  disabled: boolean;
}): JSX.Element {
  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit();
  }
  return (
    <form
      role="search"
      onSubmit={submit}
      className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-2"
    >
      <button
        type="submit"
        aria-label="Search"
        title="Search"
        disabled={disabled || searching || value.trim() === ''}
        className="flex size-7 shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 disabled:pointer-events-none disabled:text-muted-foreground"
      >
        <SearchIcon className="size-4" aria-hidden />
      </button>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        disabled={disabled || searching}
        className="h-8 border-0 shadow-none focus-visible:ring-0"
      />
      {searching && (
        <Loader2Icon
          className="size-4 shrink-0 animate-spin text-muted-foreground"
          aria-label="Searching"
        />
      )}
      {showClear && !searching && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Clear search"
          onClick={onClear}
        >
          <XIcon aria-hidden />
        </Button>
      )}
    </form>
  );
}
